import { Box, Collapse, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import Uppy, { Body, Meta, UppyFile } from '@uppy/core';
import type { Skin } from '@emoji-mart/data';
import type { RichTextEditorRef } from 'mui-tiptap';

import RichTextEditor from './RichTextEditor';
import EmojiPicker from './EmojiPicker';
import Controls from './Controls';

import useZDUser from 'services/help-center/queries/useZDUser';
import useSendMessage from 'services/help-center/mutations/useSendComment';
import useTicket from 'services/help-center/queries/useTicket';
import { collectiveTracking } from 'modules/common/components/AnalyticsTracking/utils';
import FilesUploader from 'modules/IMC/MessageCenter/screens/NewMessage/components/FilesUploader';
import mapZendeskErrorToMessage, { handleUploadErrors } from 'modules/IMC/MessageCenter/utils/zendeskErrorHandler';

interface MessageInputProps {
    ticketId: number;
}

export default function MessageInput({ ticketId }: MessageInputProps) {
    const { data: zdUser } = useZDUser();

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [showControls, setShowControls] = useState<boolean>(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
    const [showFileUploader, setShowFileUploader] = useState<boolean>(false);
    const [files, setFiles] = useState<UppyFile<Meta, Body>[]>([]);
    const { mutateAsync: sendMessage } = useSendMessage();
    const { data: ticketData, isLoading } = useTicket(ticketId);
    const ticket = ticketData?.ticket;

    const rteRef = useRef<RichTextEditorRef>(null);
    const uppyRef = useRef<Uppy | null>(null);
    const { enqueueSnackbar } = useSnackbar();

    const toggleOptions = () => setShowControls((prev) => !prev);
    const toggleEmojiPicker = () => setShowEmojiPicker((prev) => !prev);
    const toggleFileUploader = () => setShowFileUploader((prev) => !prev);

    const handleSendMessage = async () => {
        setIsSubmitting(true);

        if (!ticket?.id) {
            enqueueSnackbar('There was an error sending your message. Please refresh the page and try again.', {
                variant: 'error',
            });
            setIsSubmitting(false);
            return;
        }

        let filesTokens: string[] = [];

        const clearAttachments = () => {
            const uppy = uppyRef.current;
            if (!uppy) {
                return;
            }
            uppy.getFiles().forEach((file) => uppy.removeFile(file.id));
            setFiles([]);
            setShowFileUploader(false);
        };

        if (uppyRef.current) {
            const uppy = uppyRef.current;
            let result;
            try {
                result = await uppy.upload();
            } catch (error) {
                enqueueSnackbar(mapZendeskErrorToMessage(error), { variant: 'error' });
                setIsSubmitting(false);
                return;
            }

            if (handleUploadErrors(result, { enqueueSnackbar })) {
                setIsSubmitting(false);
                return;
            }

            filesTokens = (result?.successful ?? []).map((file: any) => file.response.body.upload.token);
        }

        try {
            await sendMessage({ ticketId: ticket.id, message, uploads: filesTokens });
            clearAttachments();
            rteRef.current?.editor?.commands.clearContent();
            setMessage('');
            setIsSubmitting(false);

            collectiveTracking.track({
                name: 'Message Sent',
                properties: {
                    category: 'message_center',
                    action: 'click',
                    conversation_id: ticket.id,
                    timestamp: new Date().toISOString(),
                    user_id: zdUser?.id,
                    has_attachment: filesTokens.length > 0,
                    message_length: message.length,
                },
            });
        } catch (error) {
            enqueueSnackbar(mapZendeskErrorToMessage(error), { variant: 'error' });
            setIsSubmitting(false);
        }
    };

    const addEmoji = (emoji: Skin) => {
        rteRef.current?.editor?.chain().focus().insertContent(emoji.native).run();
        setShowEmojiPicker(false);
    };

    // A ticket awaiting its CSAT rating stays replyable: a reply reopens it in Zendesk, so a member
    // who still needs help isn't left with only the survey comment to answer in.
    if (ticket?.status === 'closed') {
        return null;
    }

    return (
        <Paper variant="outlined" sx={{ paddingX: 2, paddingY: 1, borderRadius: 2, position: 'relative' }}>
            <RichTextEditor ref={rteRef} onUpdate={setMessage} showControls={showControls} disabled={isLoading} />
            {showEmojiPicker && <EmojiPicker onSelectEmoji={addEmoji} />}

            <Collapse in={showFileUploader}>
                <Box
                    sx={{
                        marginY: 1,
                        '& .uppy-Dashboard-AddFiles, & .uppy-Dashboard-dropFilesHereHint': {
                            height: '130px !important',
                        },
                    }}>
                    <FilesUploader uppyRef={uppyRef} onFilesChange={setFiles} disabled={isSubmitting} />
                </Box>
            </Collapse>
            <Controls
                showControls={showControls}
                toggleControls={toggleOptions}
                showEmojiPicker={showEmojiPicker}
                toggleEmojiPicker={toggleEmojiPicker}
                showFileUploader={showFileUploader}
                toggleFileUploader={toggleFileUploader}
                filesCount={files.length}
                onSendMessage={handleSendMessage}
                canSend={!!message && !isSubmitting}
                disableButtons={isLoading}
            />
        </Paper>
    );
}
